import { useMemo, type ReactNode } from 'react';
import { DashboardDataTable, useDashboardDataTableState } from '@shigo-ui/dashboard-ui';

import { networkPerformancePageSizes, type NetworkPerformanceRow } from '../../model';
import { dashboardT } from '../../../../../lib/messages';
import { networkPerformanceColumns } from './columns';
import { sortNetworkPerformanceRows } from './sorting';

interface NetworkPerformanceTableProps {
  rows: NetworkPerformanceRow[];
  loading: boolean;
  errorMessage?: string | undefined;
  controls: ReactNode;
}

export function NetworkPerformanceTable({
  rows,
  loading,
  errorMessage,
  controls,
}: NetworkPerformanceTableProps) {
  const table = useDashboardDataTableState({
    initialPageSize: 25,
    totalItems: rows.length,
    pageSizes: networkPerformancePageSizes,
    disabled: loading,
  });
  const { page: safePage, pageSize, sort } = table;
  const sortedRows = useMemo(() => sortNetworkPerformanceRows(rows, sort), [rows, sort]);
  const visibleRows = sortedRows.slice(safePage * pageSize, (safePage + 1) * pageSize);

  return (
    <DashboardDataTable
      title={dashboardT('nav.reporting.networkPerformance')}
      tableLabel={dashboardT('nav.reporting.networkPerformance')}
      filterToolbar={controls}
      columns={networkPerformanceColumns}
      items={visibleRows}
      getRowKey={(row) => `${row.networkId}-${String(row.year)}-${row.month}-${row.half}`}
      loading={loading}
      loadingMessage={dashboardT('networkPerformance.loading')}
      errorMessage={errorMessage}
      emptyMessage={dashboardT('networkPerformance.empty')}
      minWidthClassName="min-w-[2500px]"
      sort={table.sortProps}
      pagination={table.pagination}
      footerSummary={`Showing ${String(
        sortedRows.length ? safePage * pageSize + 1 : 0,
      )}-${String(Math.min((safePage + 1) * pageSize, sortedRows.length))} of ${String(
        sortedRows.length,
      )}`}
    />
  );
}
